import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion as MOTION } from "framer-motion";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import resolveImageUrl from "../../utils/imageUtils";
import { Toaster, toast } from "react-hot-toast";

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

// Helper functions for area categories
const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const inferLandAreaSqYards = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  const rounded = Math.round(n);
  const isEffectivelyInteger = Math.abs(n - rounded) < 1e-6;
  if (n > 108000) return n / 9;
  if (n > 3000 && isEffectivelyInteger && rounded % 9 === 0) return n / 9;
  return n;
};

const inferCoveredAreaSqFt = (raw) => {
  const n = toFiniteNumber(raw);
  if (n === null || n < 0) return null;
  if (n <= 300) return n * 9;
  return n;
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        const n = Number(String(ml ?? "").replaceAll(",", "").trim());
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        const n = Number(String(ml ?? "").replaceAll(",", "").trim());
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
  }

  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      // Check if realValue is a range string before trying to format as number
      const strVal = String(realValue);
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();

  // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  // Check for "greater than X" or "more than X"
  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  // Check for "less than X" or "under X"
  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  // Check for "between X and Y"
  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  // Try to parse as a single number
  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const areValuesDifferent = (adminVal, mlVal, field) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    // Try to extract number for area fields
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  const a = normalize(adminVal);

  // For landarea and coveredarea, check if admin is within ML range
  if ((field === "landarea" || field === "coveredarea") && mlVal) {
    const range = parseRange(mlVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value; // highlight if admin is NOT greater
        case "less":
          return a >= range.value; // highlight if admin is NOT less
        case "between":
          return a < range.min || a > range.max; // highlight if outside range
        case "exact":
          return Math.abs(a - range.value) > 1e-9; // highlight if different
      }
    }
  }

  // For all other fields, use original comparison
  const m = normalize(mlVal);
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};

const Property = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const itemsPerPage = 12;

  // Search state
  const [searchPin, setSearchPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [searchedProperties, setSearchedProperties] = useState(null);
  const [inspectorWorkflowFilter, setInspectorWorkflowFilter] = useState("all"); // all | inspector6 | inspector9

  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [highlightPin, setHighlightPin] = useState("");

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const highlightPropertyCard = (pin) => {
    const normalized = normalizePin(pin);
    if (!normalized) return;
    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;

  const getPropertyImage = (p) => {
    if (!p) return null;
    if (p.image) return p.image;
    if (p.image_url) return p.image_url;
    if (p.imageUrl) return p.imageUrl;
    if (p.image_path) return p.image_path;
    if (Array.isArray(p.images) && p.images.length > 0) return p.images[0];
    if (Array.isArray(p.property_images) && p.property_images.length > 0) {
      const first = p.property_images[0];
      return first?.image || first?.url || first?.path || null;
    }
    return null;
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);

    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, {
          pin: selectedProperty.pin,
          rotateBy: 90,
          forcePortrait: false
        });
      }

      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSearchedProperties((prev) =>
        prev ? prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p)) : null
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  // Fetch circles
  const fetchCircles = async () => {
    try {
      const res = await axios.get("/properties/circles");
      setCircles(res.data || []);
    } catch {
      console.error("Failed to load circles");
    }
  };

  // Fetch resolved properties
  const fetchProperties = async (page = 1) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const currentUserId = user?.id;

      const res = await axios.get("/properties", {
        params: {
          status: "resolved",
          page,
          limit: itemsPerPage,
          circle: selectedCircle || undefined
        },
      });

      // Only keep properties assigned directly to this supervisor
      const list = (res.data?.data || []).filter((p) => p.supervisor_user_id === currentUserId);
      const toTime = (val) => {
        const t = val ? new Date(val).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      };
      const sorted = [...list].sort((a, b) => {
        const at =
          toTime(a.last_action_at) ||
          toTime(a.reassessment_date) ||
          toTime(a.inspector_explanation_created_at) ||
          toTime(a.supervisor_assigned_at) ||
          (Number(a.id) || 0);
        const bt =
          toTime(b.last_action_at) ||
          toTime(b.reassessment_date) ||
          toTime(b.inspector_explanation_created_at) ||
          toTime(b.supervisor_assigned_at) ||
          (Number(b.id) || 0);
        if (bt !== at) return bt - at;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      });

      // setProperties(sorted);
      const fetchBust = Date.now();
      setProperties(sorted.map((p) => ({ ...p, _imgBust: p._imgBust ?? fetchBust })));
      setTotalPages(res.data.pagination.pages || 1);
      setCurrentPage(res.data.pagination.page || 1);
      setSearchedProperties(null);
      setSearchMessage("");
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    }
  };

  // Search handler — searches resolved properties by PIN
  const handleSearchByPin = async () => {
    const pin = searchPin.trim();
    if (!pin) {
      setSearchMessage("Please enter a PIN.");
      setSearchedProperties(null);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const currentUserId = user?.id;

      setSearching(true);
      setSearchMessage("");
      setSearchedProperties(null);

      // Fetch all resolved properties
      const res = await axios.get("/properties", {
        params: {
          status: "resolved",
          limit: 10000,
          circle: selectedCircle || undefined
        },
      });

      const list = (res.data?.data || []).filter((p) => p.supervisor_user_id === currentUserId);
      const found = list.find(
        (p) => String(p.pin || '').trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage("No resolved property found with this PIN.");
        setSearchedProperties([]);
        return;
      }

      setSearchedProperties([found]);
      setSearchMessage(`Found 1 property with PIN: ${pin}`);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setSearchMessage("Failed to search property by PIN.");
    } finally {
      setSearching(false);
    }
  };

  // Clear search and reset to all properties
  const handleClearSearch = () => {
    setSearchPin("");
    setSearchMessage("");
    setSearchedProperties(null);
    setSelectedCircle("");
    fetchProperties(1);
  };

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
    setRotation(0);
  };

  const closeModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setIsModalOpen(false);
    setSelectedProperty(null);
    setRotation(0);
    setTimeline([]);
    setTimelineError("");
    setTimelineLoading(false);
    highlightPropertyCard(pinToHighlight);
  };

  useEffect(() => {
    const pin = selectedProperty?.pin;
    if (!isModalOpen || !pin) {
      setTimeline([]);
      setTimelineError("");
      setTimelineLoading(false);
      return;
    }

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const params = selectedProperty?.id ? { propertyId: selectedProperty.id } : {};
        const res = await axios.get(`/properties/timeline-id/${selectedProperty.id}`, { params });
        setTimeline(res.data?.timeline || []);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setTimeline([]);
        setTimelineError("Failed to load previous comments.");
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [isModalOpen, selectedProperty?.pin, selectedProperty?.id]);

  useEffect(() => {
    fetchCircles();
    fetchProperties(currentPage);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchProperties(1);
  }, [selectedCircle]);

  // Auto-open from PIN query
  useEffect(() => {
    if (!targetPinFromQuery || !properties.length) return;
    if (autoOpenedPin === targetPinFromQuery) return;

    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === targetPinFromQuery.toLowerCase()
    );

    if (matched) {
      setSelectedProperty(matched);
      setIsModalOpen(true);
      setAutoOpenedPin(targetPinFromQuery);
    }
  }, [targetPinFromQuery, properties, autoOpenedPin]);

  // Reset to first page when search results change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchedProperties, inspectorWorkflowFilter]);

  // NOTE: getWorkflowInspectorUsername is declared here, BEFORE it is first used
  // below in `displayProperties`. This ordering fixes a
  // "Cannot access before initialization" (TDZ) crash that previously occurred
  // only when clicking the Inspector 6 / Inspector 9 filter buttons.
  const getWorkflowInspectorUsername = (prop) =>
    String(prop?.workflow_inspector_username || "").trim().toLowerCase();
  const getUpdatedByUsername = (prop) => {
    const updatedBy = prop?.updated_by;
    if (updatedBy === "24") return "inspector6";
    if (updatedBy === "25") return "inspector9";
    return String(prop?.workflow_inspector_username || "").trim().toLowerCase();
  };

  // Get the properties to display (either search results or all properties)
  const baseProperties = searchedProperties !== null ? searchedProperties : properties;
  const displayProperties =
    inspectorWorkflowFilter === "all"
      ? baseProperties
      : baseProperties.filter(
        (p) => getUpdatedByUsername(p) === inspectorWorkflowFilter
      );
  const displayTotalPages = Math.ceil(displayProperties.length / itemsPerPage) || 1;

  const isReassessYes = (value) => {
    if (value == 1) return true;
    const str = String(value ?? "").trim().toLowerCase();
    return str === "1" || str === "true" || str === "yes";
  };

  const inspector6Count = properties.filter(
    (p) => getUpdatedByUsername(p) === "inspector6"
  ).length;
  const inspector9Count = properties.filter(
    (p) => getUpdatedByUsername(p) === "inspector9"
  ).length;

  const hasValidImage = (prop) => {
    const img = getPropertyImage(prop);
    if (!img) return false;
    return !imageLoadErrors[prop.pin];
  };

  const hasMLData = (property) => {
    return (
      hasUsableValue(getMLDisplayValue(property, "floor")) &&
      getMLDisplayValue(property, "floor") !== "N/A"
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <Sidebar />
      <Toaster position="top-right" />

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-teal-700">
            All Resolved Properties
          </h1>
          <p className="text-gray-600 mt-3 text-base italic border-l-4 border-blue-400 pl-4 bg-blue-50 py-2 rounded-r">
            Hello! You have accepted the assessments of these properties and hence they have been marked as resolved.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h2 className="text-md font-semibold text-gray-800 mb-3">Search Property by PIN</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={searchPin}
                onChange={(e) => setSearchPin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchByPin();
                  }
                }}
                placeholder="Enter exact PIN..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <button
                onClick={handleSearchByPin}
                disabled={searching}
                className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60 transition-colors"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
              {(searchMessage && searchMessage.includes("Found")) && (
                <button
                  onClick={handleClearSearch}
                  className="px-5 py-2 rounded-lg bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
            {searchMessage && (
              <p className={`mt-3 text-sm ${searchMessage.includes("Found") ? "text-green-600" : "text-red-600"}`}>
                {searchMessage}
              </p>
            )}
          </div>
        </div>

        {/* Circle Filter - Only show when not searching */}
        {!searchedProperties && (
          <div className="flex flex-wrap justify-center gap-4 mb-8 bg-white shadow-md rounded-xl p-4 border border-teal-100">
            <select
              value={selectedCircle}
              onChange={(e) => setSelectedCircle(e.target.value)}
              className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-gray-50 shadow-sm min-w-[180px]"
            >
              <option value="">All Circles</option>
              {circles.map((circle, i) => (
                <option key={i} value={circle}>
                  {circle}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Inspector workflow filter - keeps inspector6 and inspector9 resolved properties from mixing */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            type="button"
            onClick={() => setInspectorWorkflowFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${inspectorWorkflowFilter === "all"
              ? "bg-teal-700 text-white"
              : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
          >
            All ({properties.length})
          </button>
          <button
            type="button"
            onClick={() => setInspectorWorkflowFilter("inspector6")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${inspectorWorkflowFilter === "inspector6"
              ? "bg-teal-700 text-white"
              : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
          >
            Inspector 6 ({inspector6Count})
          </button>
          <button
            type="button"
            onClick={() => setInspectorWorkflowFilter("inspector9")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${inspectorWorkflowFilter === "inspector9"
              ? "bg-teal-700 text-white"
              : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
          >
            Inspector 9 ({inspector9Count})
          </button>
        </div>

        {/* Properties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayProperties.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {searchedProperties !== null ? "No Property Found" : "No Properties Found"}
              </h3>
              <p className="text-gray-500 max-w-sm mx-auto">
                {searchedProperties !== null
                  ? "No resolved property found with the entered PIN."
                  : "No resolved properties found for the selected filters."}
              </p>
              {searchedProperties !== null && (
                <button
                  onClick={handleClearSearch}
                  className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                >
                  View All Properties
                </button>
              )}
            </div>
          ) : (
            displayProperties.map((prop) => {
              const isSelected = normalizePin(prop.pin) && normalizePin(prop.pin) === highlightPin;

              return (
                <div
                  key={prop.id}
                  onClick={() => handlePropertyClick(prop)}
                  data-pin={normalizePin(prop.pin)}
                  className={`bg-white shadow cursor-pointer transition-all hover:shadow-lg hover:border-teal-300 hover:scale-[1.02] relative rounded-xl ${isSelected ? "border-2 border-sky-500" : "border border-gray-200"
                    }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  {hasValidImage(prop) ? (
                    <img
                      src={appendCacheBuster(resolveImageUrl(getPropertyImage(prop)), prop._imgBust)}
                      alt="Property"
                      className="w-full h-40 object-cover rounded-t-xl"
                      onError={() =>
                        setImageLoadErrors((prev) => ({ ...prev, [prop.pin]: true }))
                      }
                    />
                  ) : (
                    <div
                      className="w-full h-40 bg-gray-200 flex items-center justify-center text-gray-500 rounded-t-xl"
                    >
                      No Image
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <h3 className="text-lg font-bold text-teal-700">
                      PIN: {prop.pin}
                    </h3>
                    <p className="text-sm">
                      <strong>Circle:</strong> {prop.circle}
                    </p>
                    {/*  <p className="text-sm">
                      <strong>Occupation:</strong> {prop.occupationstatus}
                    </p>
                    <p className="text-sm">
                      <strong>Floors:</strong> {prop.floor_admin || "-"}
                    </p>
                    <p className="text-sm">
                      <strong>Land Use:</strong> {prop.landuse_admin || "-"}
                    </p>
                    <p className="text-sm">
                      <strong>Land Area:</strong> {prop.landarea_admin || "-"}
                    </p>
                    <p className="text-sm">
                      <strong>Covered Area:</strong>{" "}
                      {prop.coveredarea_admin || "-"}
                    </p> */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="inline-block text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        Status: {prop.status}
                      </span>
                      <span
                        className={`inline-block text-xs px-2 py-1 rounded-full ${isReassessYes(prop.reassess)
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {isReassessYes(prop.reassess) ? "Reassessed" : "Not Reassessed"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {displayTotalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: displayTotalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${currentPage === i + 1
                  ? "bg-teal-700 text-white"
                  : "bg-gray-200 text-gray-800"
                  }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, displayTotalPages))
              }
              className="px-3 py-1 bg-teal-600 text-white rounded disabled:opacity-50"
              disabled={currentPage === displayTotalPages}
            >
              Next
            </button>
          </div>
        )}
      </main>

      {/* Property Detail Modal with Table Format */}
      <AnimatePresence>
        {isModalOpen && selectedProperty && (
          <MOTION.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-start md:items-center overflow-auto z-50 p-4"
            onClick={closeModal}
          >
            <MOTION.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-4xl rounded-xl shadow-2xl relative h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 pr-14">
                {/* Header Info */}
                <div className="mb-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-gray-500 text-sm">Circle</p>
                      <p className="text-lg font-bold text-gray-800 uppercase">{selectedProperty.circle || "N/A"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500 text-sm mb-1">Reassess Status</p>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${selectedProperty.reassess == 1
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {selectedProperty.reassess == 1 ? "✓ Reassessed" : "✗ Not Reassessed"}
                      </span>
                      {selectedProperty.reassess == 1 && selectedProperty.reassessment_date && (
                        <p className="mt-2 text-xs text-gray-500">
                          Reassessed At:{" "}
                          <span className="font-semibold text-gray-700">
                            {new Date(selectedProperty.reassessment_date).toLocaleString()}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                  {/*  <div className="flex justify-between items-center text-sm text-gray-500">
                    <p>PIN: <span className="font-semibold text-gray-700">{selectedProperty.pin}</span></p>
                    <p>Updated By: <span className="font-semibold text-gray-700">{selectedProperty.updated_by || "N/A"}</span></p>
                  </div> */}
                </div>

                {/* {selectedProperty.supervisor_flag_comment && (
                  <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-yellow-800">Supervisor Flag Comment</h3>
                    </div>
                    <p className="text-yellow-700 text-sm whitespace-pre-wrap">
                      {selectedProperty.supervisor_flag_comment}
                    </p>
                  </div>
                )}
                {(selectedProperty.supervisor_reason || selectedProperty.reason) && (
                  <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-red-800">Supervisor Comment</h3>
                      {selectedProperty.supervisor_assigned_at && (
                        <div className="text-sm text-red-700">
                          {new Date(selectedProperty.supervisor_assigned_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <p className="text-red-700 text-sm whitespace-pre-wrap">
                      {selectedProperty.supervisor_reason || selectedProperty.reason}
                    </p>
                  </div>
                )} */}

                {(() => {
                  const agreementItem = (timeline || []).find(
                    (item) => item?.type === "algorithm_response"
                  );
                  if (!agreementItem) return null;
                  const stage = agreementItem.stage || "";
                  let agreementText = "";
                  if (stage.includes("(Agreed)")) agreementText = "(Agreed)";
                  else if (stage.includes("(Disagreed)")) agreementText = "(Disagreed)";
                  else if (stage.includes("(Partially Agreed)")) agreementText = "(Partially Agreed)";
                  const createdAt = agreementItem?.created_at
                    ? new Date(agreementItem.created_at).toLocaleString()
                    : "";
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 shadow-sm">
                      {/* <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-blue-900">Computer Vision Agreement</h3>
                        {createdAt && <div className="text-xs text-blue-700">{createdAt}</div>}
                      </div> */}
                      <p className="text-blue-800 text-sm whitespace-pre-wrap">
                        Did inspector agree with Computer Vision Assessment?
                        <br />
                        {agreementText}
                      </p>
                    </div>
                  );
                })()}


                {(selectedProperty.inspector_overall_explanation ||
                  selectedProperty.inspector_floor_explanation ||
                  selectedProperty.inspector_landuse_explanation ||
                  selectedProperty.inspector_landarea_explanation ||
                  selectedProperty.inspector_coveredarea_explanation) && (
                    <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">Inspector Comments</h3>
                        {selectedProperty.inspector_explanation_created_at && (
                          <div className="text-sm text-gray-500">
                            {new Date(selectedProperty.inspector_explanation_created_at).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {selectedProperty.inspector_overall_explanation && (
                        <div className="mb-3">
                          <div className="text-sm font-semibold text-gray-700">Overall</div>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap">
                            {selectedProperty.inspector_overall_explanation}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedProperty.inspector_floor_explanation && (
                          <div className="border border-gray-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-gray-700">Floor</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_floor_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_landuse_explanation && (
                          <div className="border border-gray-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-gray-700">Land Use</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_landuse_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_landarea_explanation && (
                          <div className="border border-gray-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-gray-700">Land Area</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_landarea_explanation}
                            </div>
                          </div>
                        )}
                        {selectedProperty.inspector_coveredarea_explanation && (
                          <div className="border border-gray-200 rounded-lg p-3">
                            <div className="text-sm font-semibold text-gray-700">Covered Area</div>
                            <div className="text-sm text-gray-800 whitespace-pre-wrap">
                              {selectedProperty.inspector_coveredarea_explanation}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Image - Clickable */}
                <div className="relative group h-96 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer mb-8">
                  {getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] ? (
                    <>
                      <div
                        className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                        style={{ transform: `rotate(${rotation}deg)` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFullImage(true);
                        }}
                      >
                        <img
                          src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
                          className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                          alt="Property"
                          onError={() =>
                            setImageLoadErrors((prev) => ({ ...prev, [selectedProperty.pin]: true }))
                          }
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                        <span className="text-white bg-black/50 px-3 py-1 rounded-full text-sm font-medium">Click to Enlarge</span>
                      </div>
                      <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                        <button
                          onClick={handleRotate}
                          className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                          title="Rotate Image"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                        {rotation !== 0 && (
                          <button
                            onClick={handleSaveRotation}
                            disabled={savingImageRotation}
                            className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                            title="Save Rotation"
                          >
                            {savingImageRotation ? (
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-60 bg-gray-200 flex items-center justify-center rounded-lg text-gray-500">
                      No Image
                    </div>
                  )}
                </div>

                {/* Property Information Table */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Property Information</h3>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      PIN: {selectedProperty.pin}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      Circle: {selectedProperty.circle || "N/A"}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      PT-5: {pickFirstValue(selectedProperty, ["refno", "pt5", "pt_5", "pt"]) || "N/A"}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      PT-6 Issue Date: {selectedProperty.pt6_issue_date ? new Date(selectedProperty.pt6_issue_date).toLocaleDateString() : "N/A"}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                      Status: {selectedProperty.status || "N/A"}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-3 text-left font-medium">Feature</th>
                          <th className="p-3 text-left font-medium">Admin</th>
                          {/* <th className="p-3 text-left font-medium">Computer Vision</th> */}
                          {selectedProperty.reassess == 1 && (
                            <th className="p-3 text-left font-medium">Reassessment</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          className={`border-b ${hasMLData(selectedProperty) &&
                            areValuesDifferent(
                              selectedProperty.floor_admin,
                              getMLDisplayValue(selectedProperty, "floor"),
                              "floor"
                            )
                            ? "bg-red-100"
                            : ""
                            }`}
                        >
                          <td className="p-3 font-medium">Floor</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.floor_admin)
                              ? selectedProperty.floor_admin
                              : "N/A"}
                          </td>
                          {/* <td className="p-3">{getMLDisplayValue(selectedProperty, "floor")}</td> */}
                          {selectedProperty.reassess == 1 && (
                            <td className="p-3">
                              {(() => {
                                const reassessVal = hasUsableValue(selectedProperty.floors_ml)
                                  ? selectedProperty.floors_ml
                                  : selectedProperty.floors_pred;
                                return hasUsableValue(reassessVal) ? reassessVal : "N/A";
                              })()}
                            </td>
                          )}
                        </tr>

                        <tr
                          className={`border-b ${hasMLData(selectedProperty) &&
                            areValuesDifferent(
                              selectedProperty.landuse_admin,
                              getMLDisplayValue(selectedProperty, "landuse"),
                              "landuse"
                            )
                            ? "bg-red-100"
                            : ""
                            }`}
                        >
                          <td className="p-3 font-medium">Land Use</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.landuse_admin)
                              ? selectedProperty.landuse_admin
                              : "N/A"}
                          </td>
                          {/* <td className="p-3">{getMLDisplayValue(selectedProperty, "landuse")}</td> */}
                          {selectedProperty.reassess == 1 && (
                            <td className="p-3">
                              {(() => {
                                const reassessVal = hasUsableValue(selectedProperty.landuse_ml)
                                  ? selectedProperty.landuse_ml
                                  : selectedProperty.land_use_pred;
                                return hasUsableValue(reassessVal) ? reassessVal : "N/A";
                              })()}
                            </td>
                          )}
                        </tr>

                        <tr
                          className={`border-b ${hasMLData(selectedProperty) &&
                            areValuesDifferent(
                              selectedProperty.landarea_admin,
                              getMLDisplayValue(selectedProperty, "landarea"),
                              "landarea"
                            )
                            ? "bg-red-100"
                            : ""
                            }`}
                        >
                          <td className="p-3 font-medium">Land Area</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.landarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.landarea_admin) || selectedProperty.landarea_admin} sq.yards`
                              : "N/A"}
                          </td>
                          {/* <td className="p-3">{getMLDisplayValue(selectedProperty, "landarea")}</td> */}
                          {selectedProperty.reassess == 1 && (
                            <td className="p-3">
                              {(() => {
                                const reassessVal = hasUsableValue(selectedProperty.landarea_ml)
                                  ? selectedProperty.landarea_ml
                                  : selectedProperty.landarea_combined_pred;
                                return formatTwoDecimals(reassessVal) || (hasUsableValue(reassessVal) ? reassessVal : "N/A");
                              })()}
                            </td>
                          )}
                        </tr>

                        <tr
                          className={`border-b ${hasMLData(selectedProperty) &&
                            areValuesDifferent(
                              selectedProperty.coveredarea_admin,
                              getMLDisplayValue(selectedProperty, "coveredarea"),
                              "coveredarea"
                            )
                            ? "bg-red-100"
                            : ""
                            }`}
                        >
                          <td className="p-3 font-medium">Covered Area</td>
                          <td className="p-3">
                            {hasUsableValue(selectedProperty.coveredarea_admin)
                              ? `${formatTwoDecimals(selectedProperty.coveredarea_admin) || selectedProperty.coveredarea_admin} sq.ft`
                              : "N/A"}
                          </td>
                          {/* <td className="p-3">{getMLDisplayValue(selectedProperty, "coveredarea")}</td> */}
                          {selectedProperty.reassess == 1 && (
                            <td className="p-3">
                              {(() => {
                                const reassessVal = hasUsableValue(selectedProperty.coveredarea_ml)
                                  ? selectedProperty.coveredarea_ml
                                  : selectedProperty.coveredarea_combined_pred;
                                return formatTwoDecimals(reassessVal) || (hasUsableValue(reassessVal) ? reassessVal : "N/A");
                              })()}
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>

              {/* Fixed Timeline Section */}
              {/* <div className="flex-shrink-0 border-t border-gray-200 bg-white rounded-b-xl p-6 pt-4 max-h-[35vh] overflow-y-auto">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">Comment Timeline</h3>
                  <div className="text-sm text-gray-500">Chat view</div>
                </div>

                {timelineLoading ? (
                  <div className="text-sm text-gray-600">Loading...</div>
                ) : timelineError ? (
                  <div className="text-sm text-red-600">{timelineError}</div>
                ) : timeline.length === 0 ? (
                  <div className="text-sm text-gray-600">No previous comments found for this property.</div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => {
                      const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                      const role = String(item.user_role || "").toLowerCase();
                      const isSupervisor = item.type === "supervisor" || role === "supervisor";
                      const isInspector = item.type === "inspector_explanation" || role === "inspector";
                      const isAction = item.type === "action";

                      const actionText = (() => {
                        if (!isAction) return "";
                        if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                        if (item.action === "ACCEPT_PROPERTY") return "Resolved (Accepted by Supervisor)";
                        if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                        return String(item.action || "Action");
                      })();

                      const rows = item?.rows || {};
                      const rowEntries = [
                        ["Floor", rows.floor],
                        ["Land Use", rows.landuse],
                        ["Land Area", rows.landarea],
                        ["Covered Area", rows.coveredarea],
                      ].filter(([, v]) => hasUsableValue(v));

                      if (isAction) {
                        return (
                          <div key={item.id} className="flex justify-center">
                            <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                              {actionText}
                              {createdAt ? ` • ${createdAt}` : ""}
                            </div>
                          </div>
                        );
                      }

                      const sideClass = isSupervisor ? "justify-start" : "justify-end";
                      const bubbleClass = isSupervisor
                        ? "bg-red-50 border-red-200 text-red-900"
                        : "bg-blue-50 border-blue-200 text-blue-900";
                      const headerLabel = isSupervisor ? "Supervisor comment" : isInspector ? "Inspector comment" : "Comment";

                      return (
                        <div key={item.id} className={`flex ${sideClass}`}>
                          <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                              <div className="text-xs font-semibold">
                                {headerLabel}
                                {item.user_name ? ` • ${item.user_name}` : ""}
                                {item.stage ? ` • ${item.stage}` : ""}
                              </div>
                              {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                            </div>

                            {item.type === "inspector_explanation" ? (
                              <div className="space-y-2">
                                {hasUsableValue(item.overall) && (
                                  <div className="text-sm whitespace-pre-wrap">{item.overall}</div>
                                )}
                                {rowEntries.length > 0 && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {rowEntries.map(([k, v]) => (
                                      <div key={k} className="bg-white/70 border border-black/5 rounded-lg p-2">
                                        <div className="text-[11px] font-semibold opacity-80">{k}</div>
                                        <div className="text-sm whitespace-pre-wrap">{v}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm whitespace-pre-wrap">{item.text || ""}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div> */}

              {/* Fixed Footer */}
              <div className="flex-shrink-0 flex justify-end p-6 pt-4 border-t border-gray-200">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </MOTION.div>
          </MOTION.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {showFullImage && selectedProperty && getPropertyImage(selectedProperty) && !imageLoadErrors[selectedProperty.pin] && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
            onClick={() => {
              setShowFullImage(false);
              setRotation(0);
            }}
          >
            <div className="absolute top-6 right-6 flex space-x-4 z-10">
              <button
                onClick={handleRotate}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                title="Rotate Image"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              {rotation !== 0 && (
                <button
                  onClick={handleSaveRotation}
                  disabled={savingImageRotation}
                  className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full transition-all shadow-lg flex items-center justify-center"
                  title="Save Rotation"
                >
                  {savingImageRotation ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  setShowFullImage(false);
                  setRotation(0);
                }}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              >
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <MOTION.div
              initial={{ scale: 0.9, rotate: 0 }}
              animate={{ scale: 1, rotate: rotation }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={appendCacheBuster(resolveImageUrl(getPropertyImage(selectedProperty)), selectedProperty._imgBust)}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                alt="Full Property"
              />
            </MOTION.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Property;
